import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex w-full flex-col rounded-xl border border-gray-200 bg-gray-50/60 p-1" }
const _hoisted_2 = {
  key: 0,
  class: "flex flex-1 flex-col items-center justify-center gap-1 py-6 text-center"
}
const _hoisted_3 = { class: "text-[11px] italic leading-tight text-gray-400" }
const _hoisted_4 = { class: "flex items-center justify-end px-1 pt-0.5" }
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = ["aria-pressed", "onClick"]

import { computed, ref } from 'vue';
import { History } from 'lucide-vue-next';
import type { Point, Series } from 'highcharts';
import GenericChart from '@/components/charts/GenericChart.vue';
import useSplineChartOptions from '@/components/charts/composables/useSplineChartOptions';
import colors from '@/plugins/tailwind';
import { i18n } from '@/plugins/internationalization/i18n';
import type { TargetSource } from '@/types/MortalityObjective';
import type { BarnHistoryRow, HistoryPointRow } from './objectiveRow';
import { formatPigsToTarget } from './objectiveDisplay';
import { isInDeviation, isWithinTarget } from './deviation';

type ChartMode = 'percent' | 'pigCount';

export default /*@__PURE__*/_defineComponent({
  __name: 'ObjectiveTrendSparkline',
  props: {
    farmName: {},
    barns: {},
    resolvedTarget: {},
    targetSource: {}
  },
  setup(__props: any) {

const props = __props;

// Fixed identity palette, deliberately excluding red/green - those stay reserved for the
// exceeded-target marker ring below, so a Barn's own colour can never be misread as a verdict.
// Highcharts cycles this array across series automatically if a Farm has more Barns than colours.
const PALETTE = [colors.blue[600], colors.purple[600], colors.amber[600], colors.teal[600], colors.pink[600], colors.indigo[600], colors.orange[600], colors.cyan[700]];

// A Barn with fewer than 2 known points can't draw a line - dropped from the chart/legend entirely
// rather than shown as a lone dot or an empty series.
const barnLines = computed(() => props.barns.filter((barn) => barn.history.filter((point) => point.currentValue != null).length >= 2));

// At least one Barn needs a drawable line, or there's nothing to chart - falls back to the muted
// empty state instead of an empty axis-less chart.
const hasEnoughHistory = computed(() => barnLines.value.length > 0);

// %<->pig-count chart mode. Not persisted (matches the un-persisted rolling/cumulative precedent in
// active-groups/Index.vue's mortalityView) - each expanded Farm tile owns its own toggle state, reset
// on remount. Percent is the default: it's the figure judged against the target everywhere else on
// this page (BarnRow/SectionRow), so a freshly expanded chart should open on the familiar reading.
const CHART_MODES: ChartMode[] = ['percent', 'pigCount'];
const chartMode = ref<ChartMode>('percent');

// Which field a point plots on the y-axis for the active mode - the marker verdict below is always
// judged off currentValue vs resolvedTarget regardless of mode, since "in Deviation" doesn't change
// meaning depending on how it's displayed.
function plottedValue(point: HistoryPointRow, mode: ChartMode): number | null {
    return mode === 'pigCount' ? point.pigsToTarget : point.currentValue;
}

function toSeriesPoint(point: HistoryPointRow, mode: ChartMode) {
    // Marker fill is the verdict (red = in Deviation, green = within target), not the Barn's
    // identity - only the dot's fill carries red/green, the connecting line/legend swatch stay the
    // Barn's own PALETTE colour so multiple Barns are still tellable apart. A solid fill on every
    // known point is a stronger signal than a ring on the bad ones alone, and doing both would be
    // redundant. No verdict (missing target) leaves the marker unstyled - inherits the series
    // colour.
    const verdictValues = { currentValue: point.currentValue, resolvedTarget: props.resolvedTarget };
    let fillColor: string | undefined;
    if (isInDeviation(verdictValues)) fillColor = colors.red[600];
    else if (isWithinTarget(verdictValues)) fillColor = colors.green[700];
    return {
        x: new Date(point.periodEnd).getTime(),
        y: plottedValue(point, mode),
        custom: { deadCount: point.deadCount, pigsToTarget: point.pigsToTarget },
        ...(fillColor ? { marker: { fillColor, lineColor: fillColor } } : {}),
    };
}

function latestKnownValue(history: HistoryPointRow[]): number | null {
    return [...history].reverse().find((point) => point.currentValue != null)?.currentValue ?? null;
}

function latestKnownPigsToTarget(history: HistoryPointRow[]): number | null {
    return [...history].reverse().find((point) => point.pigsToTarget != null)?.pigsToTarget ?? null;
}

// Point/legend value text for the active mode - pulled out of the tooltip's pointFormatter below so
// the null-check and the mode branch don't nest into one another.
function formatPointValue(y: number | null | undefined, mode: ChartMode): string {
    if (y == null) return '—';
    return mode === 'pigCount' ? formatPigsToTarget(y) : `${i18n.global.n(y, 'decimal')}%`;
}

// The tooltip below renders via `useHTML: true`, so any untrusted string it interpolates must be
// escaped first - `p.series.name` traces back to `barn.name`, an admin-entered Location display name
// from the wire response, never sanitized upstream. Without this, a Barn name containing markup would
// execute for any viewer who hovers that line.
function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]!));
}

const chartOptions = computed(() =>
    useSplineChartOptions({
        // Explicit height, not a Tailwind class on GenericChart's wrapper: Highcharts measures/sizes
        // its actual chartElement once at mount and doesn't reliably pick up a CSS height applied two
        // ancestors up through a grid container with no explicit row sizing, so a wrapper height
        // alone renders the chart taller than its box, overlapping the next Farm's row. `chart.height`
        // is what Highcharts itself honors.
        chart: { height: 280, spacing: [8, 8, 4, 8] },
        title: { text: i18n.global.t('views.objectives.texts.trendLabel'), style: { fontSize: '12px', fontWeight: '700' } },
        subtitle: { text: props.farmName, style: { fontSize: '10.5px' } },
        colors: PALETTE,
        xAxis: {
            type: 'datetime',
            // Independent of the header's date-range picker - this feature's window is always the
            // selected reference period, never the global picker; the base composable's min/max come
            // from that picker, so they're explicitly cleared here.
            min: null,
            max: null,
            tickInterval: null,
            crosshair: true,
            title: { text: '' },
            labels: { format: '{value:%b %d}', rotation: 0, style: { fontSize: '9px' } },
        },
        yAxis: {
            title: { text: '' },
            // No `%` suffix in pig-count mode - the axis is a plain pig count there.
            labels: { format: chartMode.value === 'pigCount' ? '{value}' : '{value}%', style: { fontSize: '9px' } },
            // Same target reference line either way, just relocated: 0 is Pigs to Target's own
            // breach boundary (see CONTEXT.md), the pig-count equivalent of "at the target %" -
            // still gated on a resolved target existing, since that's what makes either figure
            // meaningful in the first place.
            plotLines:
                props.resolvedTarget != null
                    ? [
                          {
                              value: chartMode.value === 'pigCount' ? 0 : props.resolvedTarget,
                              color: colors.gray[400],
                              width: 1,
                              zIndex: 3,
                              dashStyle: props.targetSource === 'Override' ? 'Solid' : 'Dash',
                              label: { text: i18n.global.t('views.objectives.texts.trendTargetLegend'), style: { fontSize: '9px', color: colors.gray[500] } },
                          },
                      ]
                    : [],
        },
        legend: {
            enabled: true,
            // HTML, not Highcharts' own plain-text renderer - needed so the parenthesised percentage
            // can carry its own verdict colour independent of the name next to it. Same untrusted-
            // input caveat as the tooltip below: `this.name` traces back to an admin-entered Barn
            // name, so it goes through the same escapeHtml before touching the returned markup.
            useHTML: true,
            itemStyle: { fontSize: '10px', fontWeight: 'normal', color: colors.gray[500] },
            itemMarginTop: 0,
            itemMarginBottom: 0,
            // `this` is always the Series here, never a Point - this chart has no pie series, the one
            // case Highcharts' own type widens `this` to `Point | Series` for.
            labelFormatter(this: Series): string {
                const name = escapeHtml(this.name);
                const latestValue = this.userOptions.custom?.latestValue;
                if (latestValue == null) return name;
                // Verdict always judged off currentValue vs resolvedTarget regardless of display
                // mode - same reasoning as the marker fill in toSeriesPoint above. Unstyled
                // (inherits itemStyle's gray) when there's no target to judge it against.
                const verdictValues = { currentValue: latestValue, resolvedTarget: props.resolvedTarget };
                let color: string | undefined;
                if (isInDeviation(verdictValues)) color = colors.red[600];
                else if (isWithinTarget(verdictValues)) color = colors.green[700];
                const latestPigsToTarget = this.userOptions.custom?.latestPigsToTarget;
                const valueText =
                    chartMode.value === 'pigCount' && latestPigsToTarget != null
                        ? `(${formatPigsToTarget(latestPigsToTarget)})`
                        : `(${i18n.global.n(latestValue, 'decimal')}%)`;
                return `${name} ${color ? `<span style="color:${color}">${valueText}</span>` : valueText}`;
            },
        },
        tooltip: {
            shared: true,
            useHTML: true,
            xDateFormat: '%b %d',
            headerFormat: '<div style="font-size:10px;font-weight:600;margin-bottom:2px">{point.key}</div>',
            pointFormatter(this: Point): string {
                // `custom` isn't a declared field of Point itself (Highcharts copies point options
                // onto the instance at runtime, but the static type only carries it on `options`) -
                // reading it off `options` keeps this typed without a cast.
                const custom = this.options.custom;
                const value = formatPointValue(this.y, chartMode.value);
                let html = `<span style="color:${this.color}">●</span> ${escapeHtml(this.series.name)}: <b>${value}</b>`;
                if (custom?.deadCount != null) html += ` &middot; ${i18n.global.t('views.objectives.texts.deadCountLabel', { count: custom.deadCount })}`;
                // Pig-count mode already plots pigsToTarget as the point's own value above - this
                // line would just repeat it, so it's percent-mode only.
                if (chartMode.value === 'percent' && custom?.pigsToTarget != null) html += `<br/><span style="color:${colors.gray[400]}">${i18n.global.t('views.objectives.texts.pigsToTargetTooltip')}: ${custom.pigsToTarget}</span>`;
                return `${html}<br/>`;
            },
        },
        series: barnLines.value.map((barn) => ({
            name: barn.name,
            connectNulls: false,
            custom: { latestValue: latestKnownValue(barn.history), latestPigsToTarget: latestKnownPigsToTarget(barn.history) },
            data: barn.history.map((point) => toSeriesPoint(point, chartMode.value)),
        })),
    })
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!hasEnoughHistory.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(History), {
            class: "h-4 w-4 text-gray-300",
            "aria-hidden": "true"
          }),
          _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('views.objectives.texts.notEnoughHistory')), 1)
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", {
              class: "inline-flex rounded-lg border border-gray-200 bg-white p-0.5",
              role: "group",
              "aria-label": _ctx.$t('views.objectives.trendMode.label')
            }, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(CHART_MODES, (mode) => {
                return _createElementVNode("button", {
                  key: mode,
                  type: "button",
                  class: _normalizeClass(["rounded-md px-2 py-0.5 text-xs font-medium transition-colors", chartMode.value === mode ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100']),
                  "aria-pressed": chartMode.value === mode,
                  onClick: ($event: any) => (chartMode.value = mode)
                }, _toDisplayString(_ctx.$t(`views.objectives.trendMode.${mode}`)), 11, _hoisted_6)
              }), 64))
            ], 8, _hoisted_5)
          ]),
          _createVNode(GenericChart, {
            class: "w-full",
            options: chartOptions.value
          }, null, 8, ["options"])
        ], 64))
  ]))
}
}

})